<template>
  <div class="merchant-container">
    <div class="side-nav">
      <MerchantSideNav />
      <MenuToggle />
    </div>
    <div class="merchant-body">
      <header>
        <MerchantHeader />
      </header>
      <div class="dashboard-content">
        <div class="row mt-5">
          <h3>Merchant KYC Verification</h3>
        </div>

        <!-- STATUS BANNER -->
        <div class="row">
          <div class="col-12 pl-0">
            <div class="card-style">
              <div class="row justify-content-between align-items-center m-0">
                <span v-if="isKycVerified" style="color: #00a800;">
                  Your KYC is verified
                  <span class="material-icons ml-1 align-middle">verified_user</span>
                  <small class="d-block text-muted">
                    Details are locked. Send an update request and the changes appear once an
                    admin approves them.
                  </small>
                </span>
                <span v-else-if="kycStatus === 'rejected'" style="color: #dc3545;">
                  Your KYC was rejected. Please correct the details and submit again.
                </span>
                <span v-else-if="hasSubmitted" style="color: #e08b00;">
                  Your KYC is under review. You can still update these details until it is verified.
                </span>
                <span v-else>
                  Please complete your KYC to activate all Amejpay merchant services.
                </span>

                <span>
                  <b-button
                    v-if="isKycVerified && isEditing"
                    variant="secondary"
                    @click="isEditing = false"
                    >Cancel Request</b-button
                  >
                  <b-button
                    v-else-if="isKycVerified"
                    variant="primary"
                    :disabled="hasPendingUpdate"
                    @click="startUpdateRequest"
                    >{{ hasPendingUpdate ? 'Update Requested' : 'Request KYC Update' }}</b-button
                  >
                  <b-button
                    v-else-if="hasSubmitted && !isEditing"
                    variant="primary"
                    @click="startEditing"
                    >Update KYC</b-button
                  >
                  <b-button
                    v-else-if="hasSubmitted && isEditing"
                    variant="secondary"
                    @click="isEditing = false"
                    >Cancel</b-button
                  >
                </span>
              </div>
              <div class="row m-0 mt-2" v-if="hasPendingUpdate">
                <small class="text-muted">
                  An update request is pending with the Admin.
                </small>
              </div>
              <div class="row m-0 mt-2" v-if="kycRemarks">
                <span><strong>Admin remarks:</strong> {{ kycRemarks }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- DETAILS VIEW : shown once anything has been submitted (pending / rejected / verified) -->
        <div class="row" v-if="showDetails">
          <div class="col-12 pl-0">
            <div class="card-style">
              <h5>Business Details</h5>
              <div class="grid-layout">
                <div class="right-side">
                  <h5 v-for="(f, i) in businessFields" :key="'bl' + i">{{ f.label }}</h5>
                </div>
                <div class="right-side">
                  <h6 v-for="(f, i) in businessFields" :key="'bv' + i">{{ f.value || '-' }}</h6>
                </div>
              </div>

              <h5 class="mt-4">Bank Details</h5>
              <div class="grid-layout">
                <div class="right-side">
                  <h5 v-for="(f, i) in bankFields" :key="'kl' + i">{{ f.label }}</h5>
                </div>
                <div class="right-side">
                  <h6 v-for="(f, i) in bankFields" :key="'kv' + i">{{ f.value || '-' }}</h6>
                </div>
              </div>
              <div class="row m-0" v-if="!hasBankDetails">
                <small class="text-muted">
                  Bank details were not returned by the API for this account.
                </small>
              </div>

              <h5 class="mt-4">Documents</h5>
              <div class="row" v-if="documents.length">
                <div
                  class="col-12 col-lg-4 mb-3"
                  v-for="(doc, i) in documents"
                  :key="'doc' + i"
                >
                  <div class="doc-card">
                    <div class="doc-title">{{ doc.label }}</div>
                    <div v-if="doc.isImage" class="doc-thumb" @click="openPreview(doc)">
                      <img :src="doc.url" :alt="doc.label" />
                      <div class="doc-zoom">Click to preview</div>
                    </div>
                    <div v-else class="doc-file">
                      <span class="material-icons-outlined">description</span>
                      <a :href="doc.url" target="_blank" rel="noopener">Open document</a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row m-0" v-else>
                <small class="text-muted">
                  No document files were returned by the API for this account.
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- FORM : first submission, a rejected KYC, or an explicit update -->
        <template v-if="showForm">
          <div class="row">
            <div class="card-style">
              <b-form @submit.stop.prevent="submitKyc" enctype="multipart/form-data">
                <h5>Business Details</h5>
                <div class="row">
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-input v-model="form.business_name" placeholder="Business Name"></b-form-input>
                    <span class="position-absolute">Business Name *</span>
                    <span class="text-danger" v-if="errors.business_name">{{ errors.business_name }}</span>
                  </div>
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-select v-model="form.business_type" :options="businessTypes"></b-form-select>
                    <span class="position-absolute">Business Type *</span>
                    <span class="text-danger" v-if="errors.business_type">{{ errors.business_type }}</span>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-input v-model="form.registration_number" placeholder="Registration Number"></b-form-input>
                    <span class="position-absolute">Registration Number *</span>
                    <span class="text-danger" v-if="errors.registration_number">{{ errors.registration_number }}</span>
                  </div>
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-input v-model="form.pan_number" placeholder="PAN Number"></b-form-input>
                    <span class="position-absolute">PAN Number *</span>
                    <span class="text-danger" v-if="errors.pan_number">{{ errors.pan_number }}</span>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 position-relative mb-3">
                    <b-form-input v-model="form.business_address" placeholder="Business Address"></b-form-input>
                    <span class="position-absolute">Business Address *</span>
                    <span class="text-danger" v-if="errors.business_address">{{ errors.business_address }}</span>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-4 position-relative mb-3">
                    <b-form-input v-model="form.city" placeholder="City"></b-form-input>
                    <span class="position-absolute">City *</span>
                    <span class="text-danger" v-if="errors.city">{{ errors.city }}</span>
                  </div>
                  <div class="col-12 col-lg-4 position-relative mb-3">
                    <SearchSelect
                      v-if="countryOptions.length"
                      v-model="form.country"
                      :options="countryOptions"
                      value-key="value"
                      label-key="text"
                      placeholder="Select country"
                      clearable
                      :has-error="!!errors.country"
                    ></SearchSelect>
                    <b-form-input v-else v-model="form.country" placeholder="Country"></b-form-input>
                    <span class="position-absolute">Country *</span>
                    <span class="text-danger" v-if="errors.country">{{ errors.country }}</span>
                  </div>
                  <div class="col-12 col-lg-4 position-relative mb-3">
                    <b-form-input v-model="form.contact_number" placeholder="ex: 9800000000"></b-form-input>
                    <span class="position-absolute">Contact Number *</span>
                    <span class="text-danger" v-if="errors.contact_number">{{ errors.contact_number }}</span>
                  </div>
                </div>

                <h5 class="mt-4">Bank Details</h5>
                <div class="row">
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-input v-model="form.bank_name" placeholder="Bank Name"></b-form-input>
                    <span class="position-absolute">Bank Name *</span>
                    <span class="text-danger" v-if="errors.bank_name">{{ errors.bank_name }}</span>
                  </div>
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-input v-model="form.bank_branch" placeholder="Branch Name"></b-form-input>
                    <span class="position-absolute">Bank Branch *</span>
                    <span class="text-danger" v-if="errors.bank_branch">{{ errors.bank_branch }}</span>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-input v-model="form.bank_account_name" placeholder="Account Holder Name"></b-form-input>
                    <span class="position-absolute">Bank Account Name *</span>
                    <span class="text-danger" v-if="errors.bank_account_name">{{ errors.bank_account_name }}</span>
                  </div>
                  <div class="col-12 col-lg-6 position-relative mb-3">
                    <b-form-input v-model="form.bank_account_number" placeholder="Account Number"></b-form-input>
                    <span class="position-absolute">Bank Account Number *</span>
                    <span class="text-danger" v-if="errors.bank_account_number">{{ errors.bank_account_number }}</span>
                  </div>
                </div>

                <h5 class="mt-4">Documents</h5>
                <div class="row">
                  <div class="col-12">
                    <small class="text-muted">Accepted formats: JPG, PNG or PDF (max 5 MB each).</small>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-4 position-relative mb-3">
                    <input
                      type="file"
                      class="form-control"
                      ref="registration_certificate"
                      accept="image/png, image/jpeg, image/jpg, application/pdf"
                      @change="onFileChange($event, 'registration_certificate')"
                    />
                    <span class="position-absolute">Registration Certificate *</span>
                    <small class="text-muted" v-if="files.registration_certificate">
                      {{ files.registration_certificate.name }}
                    </small>
                    <span class="text-danger" v-if="errors.registration_certificate">{{ errors.registration_certificate }}</span>
                  </div>
                  <div class="col-12 col-lg-4 position-relative mb-3">
                    <input
                      type="file"
                      class="form-control"
                      ref="pan_certificate"
                      accept="image/png, image/jpeg, image/jpg, application/pdf"
                      @change="onFileChange($event, 'pan_certificate')"
                    />
                    <span class="position-absolute">PAN Certificate *</span>
                    <small class="text-muted" v-if="files.pan_certificate">
                      {{ files.pan_certificate.name }}
                    </small>
                    <span class="text-danger" v-if="errors.pan_certificate">{{ errors.pan_certificate }}</span>
                  </div>
                  <div class="col-12 col-lg-4 position-relative mb-3">
                    <input
                      type="file"
                      class="form-control"
                      ref="owner_id_document"
                      accept="image/png, image/jpeg, image/jpg, application/pdf"
                      @change="onFileChange($event, 'owner_id_document')"
                    />
                    <span class="position-absolute">Owner ID Document *</span>
                    <small class="text-muted" v-if="files.owner_id_document">
                      {{ files.owner_id_document.name }}
                    </small>
                    <span class="text-danger" v-if="errors.owner_id_document">{{ errors.owner_id_document }}</span>
                  </div>
                </div>

                <div class="row mt-3">
                  <div class="col">
                    <b-button variant="primary" type="submit" :disabled="isLoading">
                      <b-spinner small v-if="isLoading" class="mr-2"></b-spinner>
                      {{ isLoading ? "Submitting..." : formSubmitLabel }}
                    </b-button>
                  </div>
                </div>
              </b-form>
            </div>
          </div>
        </template>
      </div>
    </div>
    <footer>
      <MerchantFooter />
    </footer>

    <!-- Document preview -->
    <b-modal id="doc-preview-modal" centered size="lg" :title="previewDoc.label" hide-footer>
      <div class="text-center">
        <img v-if="previewDoc.url" :src="previewDoc.url" class="preview-img" :alt="previewDoc.label" />
        <div class="mt-2">
          <a :href="previewDoc.url" target="_blank" rel="noopener">Open in new tab</a>
        </div>
      </div>
    </b-modal>

    <b-toast id="kyc-success-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Success</strong>
      </div>
      <div><p>{{ successText }}</p></div>
    </b-toast>
    <b-toast id="kyc-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error</strong>
      </div>
      <div><p>{{ message }}</p></div>
    </b-toast>
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import MerchantSideNav from "./MerchantSideNav";
import MerchantHeader from "./MerchantHeader";
import MerchantFooter from "./MerchantFooter";
import MenuToggle from "./MenuToggle";
import SearchSelect from "../SearchSelect.vue";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default {
  name: "MerchantKyc",
  components: { MerchantSideNav, MerchantHeader, MerchantFooter, MenuToggle, SearchSelect },
  data() {
    return {
      isLoading: false,
      isEditing: false,
      successText: "",
      errors: {},
      previewDoc: { label: "", url: "" },
      form: {
        business_name: "",
        business_type: "",
        registration_number: "",
        pan_number: "",
        business_address: "",
        city: "",
        country: "",
        contact_number: "",
        bank_name: "",
        bank_branch: "",
        bank_account_name: "",
        bank_account_number: "",
      },
      files: {
        registration_certificate: null,
        pan_certificate: null,
        owner_id_document: null,
      },
      businessTypes: [
        { value: "", text: "Select Business Type" },
        { value: "sole_proprietorship", text: "Sole Proprietorship" },
        { value: "partnership", text: "Partnership" },
        { value: "private_limited", text: "Private Limited" },
        { value: "public_limited", text: "Public Limited" },
        { value: "ngo_ingo", text: "NGO / INGO" },
        { value: "cooperative", text: "Cooperative" },
        { value: "other", text: "Other" },
      ],
      documentLabels: {
        registration_certificate: "Registration Certificate",
        pan_certificate: "PAN Certificate",
        owner_id_document: "Owner ID Document",
      },
      valid_phone: /^\+?[0-9\s-]{6,20}$/,
    };
  },
  computed: {
    ...mapState([
      "profile", "countries", "kycdata",
      "successmessage", "errormessage", "message", "registerErrors",
    ]),
    author: function () {
      return (this.profile && this.profile.author) ? this.profile.author : {};
    },
    merchant: function () {
      return this.author.merchant ? this.author.merchant : {};
    },
    application: function () {
      return this.merchant.application ? this.merchant.application : {};
    },
    /**
     * GET /v1/merchant/kyc returns the FULL record (bank details + document files).
     * GET /v1/profile only nests a short summary, which is why bank details and
     * file previews were missing. Prefer the full record, fall back to the summary.
     */
    kycInfo: function () {
      const summary = this.merchant.kyc ? this.merchant.kyc : {};
      let full = this.kycdata;
      if (Array.isArray(full)) { full = full.length ? full[0] : {}; }
      if (full && typeof full === "object" && (full.data || full.kyc)) {
        full = full.data || full.kyc;
      }
      if (!full || typeof full !== "object") { full = {}; }
      return Object.assign({}, summary, full);
    },
    isKycVerified: function () {
      return this.author.is_kyc_verified === true;
    },
    kycStatus: function () {
      return this.kycInfo.status || this.author.kyc_status || "";
    },
    kycRemarks: function () {
      return this.kycInfo.remarks || this.kycInfo.remark || "";
    },
    hasSubmitted: function () {
      const s = this.kycStatus;
      if (s && s !== "not_submitted") { return true; }
      return !!this.kycInfo.business_name;
    },
    showDetails: function () {
      return this.hasSubmitted;
    },
    /**
     * Verified records are locked, but the merchant can still open the SAME full form to
     * send an update request to /merchant/kyc/update-requests.
     */
    isUpdateRequest: function () {
      return this.isKycVerified && this.isEditing;
    },
    hasPendingUpdate: function () {
      const k = this.kycInfo;
      return !!(k.pending_update_request || k.has_pending_update_request);
    },
    showForm: function () {
      if (this.isKycVerified) { return this.isEditing; }
      return !this.hasSubmitted || this.isEditing;
    },
    formSubmitLabel: function () {
      return this.isUpdateRequest ? "Send Update Request" : (this.isKycPending ? "Update KYC" : "Submit KYC");
    },
    businessFields: function () {
      const k = this.kycInfo;
      const pretty = function (v) {
        if (!v) { return ""; }
        return String(v).split("_").map(function (w) {
          return w.charAt(0).toUpperCase() + w.slice(1);
        }).join(" ");
      };
      return [
        { label: "Merchant Code", value: k.merchant_code },
        { label: "Business Name", value: k.business_name },
        { label: "Business Type", value: pretty(k.business_type) },
        { label: "Registration Number", value: k.registration_number },
        { label: "PAN Number", value: k.pan_number },
        { label: "Business Address", value: k.business_address },
        { label: "City", value: k.city },
        { label: "Country", value: k.country },
        { label: "Contact Number", value: k.contact_number },
        { label: "KYC Status", value: k.status || this.kycStatus },
      ];
    },
    bankFields: function () {
      const k = this.kycInfo;
      return [
        { label: "Bank Name", value: k.bank_name },
        { label: "Bank Branch", value: k.bank_branch },
        { label: "Bank Account Name", value: k.bank_account_name },
        { label: "Bank Account Number", value: k.bank_account_number },
      ];
    },
    hasBankDetails: function () {
      return this.bankFields.some(function (f) { return !!f.value; });
    },
    /**
     * Builds the document list defensively: the API may return a plain URL string,
     * an object like { file: url } / { url: ... }, or a `documents` array.
     */
    documents: function () {
      const k = this.kycInfo;
      const vm = this;
      const out = [];
      const pushDoc = function (label, raw) {
        const url = vm.extractUrl(raw);
        if (!url) { return; }
        out.push({ label: label, url: url, isImage: vm.isImageUrl(url) });
      };
      Object.keys(this.documentLabels).forEach(function (key) {
        const label = vm.documentLabels[key];
        pushDoc(label, k[key] || k[key + "_url"] || k[key + "_file"]);
      });
      const list = k.documents || k.files;
      if (Array.isArray(list)) {
        list.forEach(function (d, i) {
          if (!d) { return; }
          const key = d.document_type || d.type || d.name || ("Document " + (i + 1));
          const label = vm.documentLabels[key] || String(key).replace(/_/g, " ");
          pushDoc(label, d.file || d.url || d.image || d);
        });
      }
      return out;
    },
    countryOptions: function () {
      const list = Array.isArray(this.countries) ? this.countries : [];
      const names = list
        .map(function (c) {
          if (typeof c === "string") { return c; }
          return c && (c.name || c.country_name || c.title || c.country);
        })
        .filter(function (n) { return !!n; });
      if (!names.length) { return []; }
      // No blank first row: SearchSelect shows a placeholder and offers a clear button.
      return names.map(function (n) { return { value: n, text: n }; });
    },
  },
  created() {
    this.listProfile();
    this.listCountries();
    this.listUserKyc();   // full KYC record: bank details + document files
    this.prefill();
  },
  watch: {
    profile: { handler: function () { this.prefill(); }, deep: true },
    kycdata: { handler: function () { this.prefill(); }, deep: true },
  },
  methods: {
    ...mapActions(["listProfile", "listCountries", "listUserKyc", "createKyc", "requestKycUpdate"]),

    extractUrl(raw) {
      if (!raw) { return ""; }
      if (typeof raw === "string") { return this.resolveUrl(raw); }
      if (typeof raw === "object") {
        const v = raw.file || raw.url || raw.image || raw.path || raw.document;
        return v ? this.resolveUrl(String(v)) : "";
      }
      return "";
    },
    resolveUrl(path) {
      if (!path) { return ""; }
      if (/^https?:\/\//i.test(path) || /^data:/i.test(path)) { return path; }
      const media = process.env.VUE_APP_MEDIA_URL || "";
      if (media) {
        return media.replace(/\/$/, "") + "/" + String(path).replace(/^\//, "");
      }
      const base = process.env.VUE_APP_SERVICE_URL || "";
      let origin = base;
      try { origin = new URL(base).origin; }
      catch (e) { origin = base.replace(/\/v1\/?$/, ""); }
      return origin.replace(/\/$/, "") + "/" + String(path).replace(/^\//, "");
    },
    isImageUrl(url) {
      return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url) || /^data:image\//i.test(url);
    },
    openPreview(doc) {
      this.previewDoc = { label: doc.label, url: doc.url };
      this.$bvModal.show("doc-preview-modal");
    },
    startEditing() {
      this.isEditing = true;
      this.prefill();
      window.scrollTo(0, 0);
    },
    /** Opens the full KYC form; submitting it posts an update REQUEST. */
    startUpdateRequest() {
      if (this.hasPendingUpdate) { return; }
      this.isEditing = true;
      this.prefill();
      window.scrollTo(0, 0);
    },

    prefill() {
      const k = this.kycInfo;
      const a = this.application;
      const f = this.form;
      if (!f.business_name) { f.business_name = k.business_name || a.company_name || ""; }
      if (!f.business_type) { f.business_type = k.business_type || ""; }
      if (!f.registration_number) { f.registration_number = k.registration_number || a.registration_number || ""; }
      if (!f.pan_number) { f.pan_number = k.pan_number || a.tax_number || ""; }
      if (!f.business_address) { f.business_address = k.business_address || a.company_address || ""; }
      if (!f.city) { f.city = k.city || a.city || ""; }
      if (!f.country) { f.country = k.country || a.country || ""; }
      if (!f.contact_number) { f.contact_number = k.contact_number || a.contact_person_number || ""; }
      if (!f.bank_name) { f.bank_name = k.bank_name || ""; }
      if (!f.bank_branch) { f.bank_branch = k.bank_branch || ""; }
      if (!f.bank_account_name) { f.bank_account_name = k.bank_account_name || ""; }
      if (!f.bank_account_number) { f.bank_account_number = k.bank_account_number || ""; }
    },

    onFileChange(event, field) {
      const file = event.target.files && event.target.files[0];
      if (!file) { return; }
      const allowed = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
      if (allowed.indexOf(file.type) === -1) {
        this.files[field] = null;
        event.target.value = "";
        this.$set(this.errors, field, "Please upload a JPG, PNG or PDF file.");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        this.files[field] = null;
        event.target.value = "";
        this.$set(this.errors, field, "File is too large. Maximum size is 5 MB.");
        return;
      }
      this.files[field] = file;
      this.$set(this.errors, field, "");
    },

    validate() {
      const f = this.form;
      const e = {};
      const required = {
        business_name: "Business Name is required.",
        business_type: "Business Type is required.",
        registration_number: "Registration Number is required.",
        pan_number: "PAN Number is required.",
        business_address: "Business Address is required.",
        city: "City is required.",
        country: "Country is required.",
        contact_number: "Contact Number is required.",
        bank_name: "Bank Name is required.",
        bank_branch: "Bank Branch is required.",
        bank_account_name: "Bank Account Name is required.",
        bank_account_number: "Bank Account Number is required.",
      };
      Object.keys(required).forEach(function (key) {
        if (!f[key]) { e[key] = required[key]; }
      });
      if (f.contact_number && !this.valid_phone.test(f.contact_number)) {
        e.contact_number = "Please enter a valid contact number.";
      }
      // On an update, keep the already-uploaded documents if none is re-selected.
      const fileLabels = {
        registration_certificate: "Registration Certificate is required.",
        pan_certificate: "PAN Certificate is required.",
        owner_id_document: "Owner ID Document is required.",
      };
      const files = this.files;
      const existing = {};
      this.documents.forEach(function (d) { existing[d.label] = true; });
      const vm = this;
      Object.keys(fileLabels).forEach(function (key) {
        const alreadyUploaded = !!existing[vm.documentLabels[key]];
        if (!files[key] && !alreadyUploaded) { e[key] = fileLabels[key]; }
      });
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    buildFormData() {
      const fd = new FormData();
      const f = this.form;
      Object.keys(f).forEach(function (key) {
        fd.append(key, String(f[key]).trim());
      });
      const files = this.files;
      Object.keys(files).forEach(function (key) {
        if (files[key]) { fd.append(key, files[key]); }
      });
      return fd;
    },

    submitKyc() {
      if (!this.validate()) {
        window.scrollTo(0, 0);
        return;
      }
      this.isLoading = true;
      const vm = this;
      const asRequest = this.isUpdateRequest;
      const payload = this.buildFormData();
      // Verified -> /merchant/kyc/update-requests ; otherwise -> /merchant/kyc
      const call = asRequest ? this.requestKycUpdate(payload) : this.createKyc(payload);
      return call
        .then(function () {
          if (vm.successmessage === "success") {
            vm.successText = vm.message || (asRequest
              ? "Your update request has been sent. The changes appear once an admin approves them."
              : "Your KYC has been submitted and is under review.");
            vm.$bvToast.show("kyc-success-toast");
            vm.isEditing = false;
            vm.listProfile();
            vm.listUserKyc();
            window.scrollTo(0, 0);
          } else {
            const fieldErrors = vm.registerErrors || {};
            Object.keys(fieldErrors).forEach(function (k) {
              vm.$set(vm.errors, k, fieldErrors[k]);
            });
            vm.$bvToast.show("kyc-error-toast");
          }
        })
        .catch(function () {
          vm.$bvToast.show("kyc-error-toast");
        })
        .then(function () {
          vm.isLoading = false;
        });
    },

  },
};
</script>

<style scoped>
.text-danger {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}
.text-muted {
  color: #6c757d;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}
.doc-card {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
  height: 100%;
}
.doc-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: #333;
}
.doc-thumb {
  cursor: pointer;
  text-align: center;
}
.doc-thumb img {
  max-width: 100%;
  max-height: 160px;
  object-fit: contain;
  border: 1px solid #eee;
  border-radius: 4px;
}
.doc-zoom {
  font-size: 12px;
  color: #0066b3;
  margin-top: 6px;
}
.doc-file {
  display: flex;
  align-items: center;
}
.doc-file .material-icons-outlined {
  margin-right: 6px;
}
.preview-img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
</style>
